import { ChevronDown, LayoutGrid } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

export const SHOOTING_BOARD_PANEL_COUNT_MIN = 2
export const SHOOTING_BOARD_PANEL_COUNT_MAX = 12

export function clampShootingBoardPanelCount(count: number): number {
  const next = Math.round(count)
  if (!Number.isFinite(next)) return SHOOTING_BOARD_PANEL_COUNT_MIN
  return Math.min(SHOOTING_BOARD_PANEL_COUNT_MAX, Math.max(SHOOTING_BOARD_PANEL_COUNT_MIN, next))
}

type ShootingBoardPanelCountPickerProps = {
  panelCount: number
  disabled?: boolean
  /** Без подписи и рамки — внутри меню параметра пульта. */
  bare?: boolean
  onChange: (panelCount: number) => void
}

export function ShootingBoardPanelCountPicker({
  panelCount,
  disabled = false,
  bare = false,
  onChange,
}: ShootingBoardPanelCountPickerProps) {
  const { t } = useTranslation(["shot"])
  const committed = clampShootingBoardPanelCount(panelCount)
  const [hover, setHover] = useState<number | null>(null)
  const [pressed, setPressed] = useState(false)
  const pressedTimer = useRef<number | null>(null)
  const display = hover ?? committed

  useEffect(() => {
    return () => {
      if (pressedTimer.current != null) window.clearTimeout(pressedTimer.current)
    }
  }, [])

  const markPressed = () => {
    if (disabled) return
    setPressed(true)
    if (pressedTimer.current != null) window.clearTimeout(pressedTimer.current)
    pressedTimer.current = window.setTimeout(() => {
      pressedTimer.current = null
      setPressed(false)
    }, 80)
  }

  return (
    <div className="inline-flex flex-col gap-1.5">
      {bare ? null : (
        <span className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
          {t("common.shootingBoardPanelCountLabel")}
        </span>
      )}
      <div
        className={cn(
          "group inline-flex flex-col items-center gap-1.5",
          !bare && "rounded-lg border border-ui-border bg-ui-surface px-2.5 py-2",
        )}
      >
        <div
          className="grid grid-cols-4"
          role="group"
          aria-label={t("common.shootingBoardPanelCount", { count: display })}
          onPointerLeave={() => setHover(null)}
        >
          {Array.from({ length: SHOOTING_BOARD_PANEL_COUNT_MAX }, (_, index) => {
            const cellCount = index + 1
            const count = clampShootingBoardPanelCount(cellCount)
            const selected = cellCount <= display
            return (
              <button
                key={cellCount}
                type="button"
                disabled={disabled}
                aria-label={t("common.shootingBoardPanelCount", { count })}
                aria-pressed={committed === cellCount}
                className={cn(
                  "relative h-5 w-5 before:pointer-events-none before:absolute before:inset-0.5 before:rounded-xs before:border before:content-['']",
                  selected
                    ? "before:border-ui-accent before:bg-ui-accent/25 group-active:before:bg-ui-accent/40"
                    : "before:border-ui-border before:bg-ui-surface-sunken",
                  selected && pressed && "before:bg-ui-accent/40",
                )}
                onPointerEnter={() => {
                  if (disabled) return
                  setHover(count)
                }}
                onPointerDown={markPressed}
                onClick={() => onChange(count)}
              />
            )
          })}
        </div>
        <p className="text-sm font-medium text-ui-text-primary tabular-nums">{display}</p>
      </div>
    </div>
  )
}

/** Количество панелей раскадровки — параметр пульта: подпись «N панели», сетка во всплывающем меню. */
export function ShootingBoardPanelCountParam({
  panelCount,
  disabled = false,
  placement = "below",
  onChange,
}: {
  panelCount: number
  disabled?: boolean
  placement?: "above" | "below"
  onChange: (panelCount: number) => void
}) {
  const { t } = useTranslation(["shot"])
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const count = clampShootingBoardPanelCount(panelCount)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: placement === "above" ? "up" : "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "auto" }),
    onOutsideClick: () => setOpen(false),
    onEscape: () => setOpen(false),
  })

  const menu =
    open && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className="rounded-lg border border-ui-border-strong p-2.5 shadow-xl shadow-black/50 backdrop-blur-md dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
      >
        <ShootingBoardPanelCountPicker
          bare
          panelCount={count}
          disabled={disabled}
          onChange={(next) => {
            onChange(next)
            setOpen(false)
          }}
        />
      </div>
    ) : null

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-label={t("common.shootingBoardPanelCountLabel")}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs whitespace-nowrap text-ui-text-secondary transition-colors hover:bg-ui-surface-hover hover:text-ui-text-primary disabled:opacity-50",
          open && "bg-ui-surface-hover text-ui-text-primary",
        )}
      >
        <LayoutGrid className="h-3 w-3 text-ui-text-muted" />
        {t("common.shootingBoardPanelCount", { count })}
        <ChevronDown className="h-3 w-3 text-ui-text-muted" />
      </button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
